/**
 * Progressive infinite scroll for collection grids.
 *
 * Loads 2 pages on landing, then 2 more each time the sentinel approaches,
 * up to 12 pages; after that a Load more button takes over (2 pages per click)
 * so the footer stays reachable on huge collections.
 *
 * Numbered pagination stays in the DOM for no-JS / crawlers. This script only
 * visually hides it while active.
 *
 * Facet/sort: Dawn FacetFiltersForm.renderProductGridContainer() replaces
 * #ProductGridContainer innerHTML and does not emit a follow-up event.
 * We abort in-flight work before that swap (wrapped) and re-init from a
 * MutationObserver on the container. Stale pages are discarded by comparing
 * filter/sort params (page stripped) against window.location.search.
 */
(function () {
  'use strict';

  var PAGES_PER_BATCH = 2;
  var AUTO_PAGE_LIMIT = 12;
  var ROOT_MARGIN = '0px 0px 600px 0px';
  var SCROLL_TRIGGER_SLACK = 600; // must match ROOT_MARGIN's bottom value

  var session = null;
  var containerObserver = null;
  var startQueued = false;
  var scrollBound = false;
  var scrollTick = false;

  function filterKey(search) {
    var raw = search || '';
    if (raw.charAt(0) === '?') raw = raw.slice(1);
    var params = new URLSearchParams(raw);
    params.delete('page');
    params.delete('section_id');
    var pairs = [];
    params.forEach(function (value, key) {
      // Empty values must not count. Applying a filter leaves the untouched
      // price fields in the address bar as `filter.v.price.gte=`, but Shopify
      // omits them from its own next-page link. Comparing them literally makes
      // one filter state look like two, every fetched page gets discarded, and
      // infinite scroll silently dies the moment a shopper ticks a filter.
      if (value === '') return;
      pairs.push(key + '=' + value);
    });
    pairs.sort();
    return pairs.join('&');
  }

  function sameFilterState(url) {
    var parsed;
    try {
      parsed = new URL(url, window.location.origin);
    } catch (err) {
      return false;
    }
    if (parsed.pathname !== window.location.pathname) return false;
    return filterKey(parsed.search) === filterKey(window.location.search);
  }

  function pageFromHref(href) {
    try {
      var page = new URL(href, window.location.origin).searchParams.get('page');
      return page ? parseInt(page, 10) : 1;
    } catch (err) {
      return NaN;
    }
  }

  /**
   * Next-page URL from server-rendered pagination.
   * Dawn's class names are reversed: paginate.previous uses
   * .pagination__item--next, paginate.next uses .pagination__item--prev.
   * Never treat --next as the URL to fetch.
   */
  function readNextUrl(root) {
    var wrapper = null;
    if (root) {
      if (root.getElementById) {
        var container = root.getElementById('ProductGridContainer');
        wrapper = container
          ? container.querySelector('.pagination-wrapper')
          : root.querySelector('#ProductGridContainer .pagination-wrapper');
      } else if (root.querySelector) {
        wrapper = root.querySelector('.pagination-wrapper');
      }
    }
    if (!wrapper) return '';

    var nextArrow = wrapper.querySelector('a.pagination__item--prev.pagination__item-arrow[href]');
    if (nextArrow) {
      var arrowHref = nextArrow.getAttribute('href');
      if (arrowHref && arrowHref !== '#') return nextArrow.href;
    }

    var current = parseInt(wrapper.getAttribute('data-page'), 10) || 1;
    var links = wrapper.querySelectorAll('a.pagination__item[href]:not(.pagination__item-arrow)');
    for (var i = 0; i < links.length; i++) {
      if (pageFromHref(links[i].href) === current + 1) return links[i].href;
    }
    return '';
  }

  function announce(sess, message) {
    if (!sess || !sess.status) return;
    sess.status.textContent = message || '';
  }

  function setLoading(sess, isLoading, addedCount) {
    if (!sess || !sess.status) return;
    if (isLoading) {
      sess.status.setAttribute('data-loading', 'true');
      announce(sess, 'Loading more products');
      if (sess.button) sess.button.setAttribute('aria-busy', 'true');
      return;
    }
    sess.status.removeAttribute('data-loading');
    if (sess.button) sess.button.removeAttribute('aria-busy');
    if (typeof addedCount === 'number' && addedCount > 0) {
      announce(
        sess,
        addedCount === 1 ? '1 product added' : addedCount + ' products added'
      );
    } else {
      announce(sess, '');
    }
  }

  function hidePagination(wrapper) {
    if (!wrapper) return;
    wrapper.classList.add('pagination-wrapper--infinite-active');
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.setAttribute('inert', '');
  }

  function restorePagination(wrapper) {
    if (!wrapper) return;
    wrapper.classList.remove('pagination-wrapper--infinite-active');
    wrapper.removeAttribute('aria-hidden');
    wrapper.removeAttribute('inert');
  }

  function replaceUrl(url) {
    try {
      var parsed = new URL(url, window.location.origin);
      if (parsed.origin !== window.location.origin) return;
      if (parsed.pathname !== window.location.pathname) return;
      var state = history.state && typeof history.state === 'object' ? Object.assign({}, history.state) : {};
      var params = new URLSearchParams(parsed.search);
      params.delete('page');
      state.searchParams = params.toString();
      history.replaceState(state, '', parsed.pathname + parsed.search + parsed.hash);
    } catch (err) {
      /* keep the current address bar if the URL cannot be parsed */
    }
  }

  function abortSession() {
    if (!session) return;
    session.dead = true;
    if (session.controller) {
      try {
        session.controller.abort();
      } catch (err) {
        /* already aborted */
      }
    }
    if (session.io) {
      session.io.disconnect();
      session.io = null;
    }
    session.inFlight = false;
  }

  function removeChrome(sess) {
    if (sess && sess.io) {
      sess.io.disconnect();
      sess.io = null;
    }
    if (sess && sess.root && sess.root.parentNode) {
      sess.root.parentNode.removeChild(sess.root);
    }
  }

  function finish(sess) {
    if (!sess || sess.dead) return;
    removeChrome(sess);
  }

  function enterButtonMode(sess) {
    if (!sess || sess.dead) return;
    if (sess.io) {
      sess.io.disconnect();
      sess.io = null;
    }
    if (sess.button) {
      sess.button.hidden = false;
      sess.button.disabled = false;
    }
  }

  /**
   * IntersectionObserver only reports CHANGES. Jump straight to the bottom of a
   * long page — the End key, a fling on a phone, or the browser restoring scroll
   * position on back-navigation — and the sentinel can land ABOVE the viewport
   * without ever being observed inside it, so no callback arrives and nothing
   * more ever loads. With pagination hidden that strands the shopper.
   * This scroll fallback re-checks the sentinel's real position, so being past
   * it counts as a trigger.
   */
  function nearSentinel(sess) {
    if (!sess || !sess.sentinel) return false;
    var rect = sess.sentinel.getBoundingClientRect();
    var viewport = window.innerHeight || document.documentElement.clientHeight || 0;
    return rect.top <= viewport + SCROLL_TRIGGER_SLACK;
  }

  function onScrollCheck() {
    var sess = session;
    if (!sess || sess.dead || sess.inFlight || !sess.io) return;
    if (scrollTick) return;
    scrollTick = true;
    window.requestAnimationFrame(function () {
      scrollTick = false;
      var live = session;
      if (!live || live.dead || live.inFlight || !live.io) return;
      if (live.pagesLoaded >= AUTO_PAGE_LIMIT) {
        enterButtonMode(live);
        return;
      }
      if (nearSentinel(live)) loadBatch(live, true);
    });
  }

  function bindScrollFallback() {
    if (scrollBound) return;
    scrollBound = true;
    window.addEventListener('scroll', onScrollCheck, { passive: true });
    window.addEventListener('resize', onScrollCheck, { passive: true });
  }

  function observe(sess) {
    if (!sess || sess.dead || !sess.sentinel) return;
    if (typeof IntersectionObserver === 'undefined') {
      enterButtonMode(sess);
      return;
    }
    bindScrollFallback();
    if (sess.io) sess.io.disconnect();
    sess.io = new IntersectionObserver(
      function (entries) {
        if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
        if (sess.dead || sess.inFlight) return;
        if (sess.pagesLoaded >= AUTO_PAGE_LIMIT) {
          enterButtonMode(sess);
          return;
        }
        loadBatch(sess, true);
      },
      { root: null, rootMargin: ROOT_MARGIN, threshold: 0 }
    );
    sess.io.observe(sess.sentinel);
  }

  function fetchAndAppend(sess, url) {
    if (sess.dead) return Promise.resolve(null);
    if (!sameFilterState(url)) return Promise.resolve(null);

    return fetch(url, { signal: sess.controller.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text().then(function (text) {
          return { text: text, finalUrl: response.url || url };
        });
      })
      .then(function (payload) {
        if (sess.dead) return null;
        if (!sameFilterState(url) || !sameFilterState(payload.finalUrl)) return null;

        var doc = new DOMParser().parseFromString(payload.text, 'text/html');
        var remoteGrid = doc.getElementById('product-grid');
        var liveGrid = document.getElementById('product-grid');
        if (!remoteGrid || !liveGrid || liveGrid !== sess.grid) return null;
        if (liveGrid.tagName !== 'UL') return null;

        var remoteItems = remoteGrid.children;
        var fragment = document.createDocumentFragment();
        for (var i = 0; i < remoteItems.length; i++) {
          if (!remoteItems[i].classList || !remoteItems[i].classList.contains('grid__item')) continue;
          var item = document.importNode(remoteItems[i], true);
          if (item.classList.contains('scroll-trigger')) {
            item.classList.add('scroll-trigger--cancel');
          }
          var nestedTriggers = item.querySelectorAll('.scroll-trigger');
          for (var t = 0; t < nestedTriggers.length; t++) {
            nestedTriggers[t].classList.add('scroll-trigger--cancel');
          }
          fragment.appendChild(item);
        }

        var count = fragment.childNodes.length;
        liveGrid.appendChild(fragment);
        sess.nextUrl = readNextUrl(doc);
        replaceUrl(payload.finalUrl);
        return count;
      });
  }

  function loadBatch(sess, auto) {
    if (!sess || sess.dead || sess.inFlight) return;
    if (!sess.nextUrl) {
      finish(sess);
      return;
    }
    if (auto && sess.pagesLoaded >= AUTO_PAGE_LIMIT) {
      enterButtonMode(sess);
      return;
    }

    sess.inFlight = true;
    if (sess.button) sess.button.disabled = true;
    setLoading(sess, true);

    var pagesThisBatch = 0;
    var added = 0;
    var discarded = false;

    function step() {
      if (sess.dead) return Promise.resolve();
      if (!sess.nextUrl) return Promise.resolve();
      if (pagesThisBatch >= PAGES_PER_BATCH) return Promise.resolve();
      if (auto && sess.pagesLoaded >= AUTO_PAGE_LIMIT) return Promise.resolve();

      var url = sess.nextUrl;
      return fetchAndAppend(sess, url).then(function (count) {
        if (count == null) {
          discarded = true;
          return;
        }
        added += count;
        pagesThisBatch += 1;
        sess.pagesLoaded += 1;
        return step();
      });
    }

    step()
      .then(function () {
        if (sess.dead) return;
        sess.inFlight = false;
        if (discarded) {
          setLoading(sess, false);
          if (sess.button) sess.button.disabled = false;
          restorePagination(sess.pagination);
          return;
        }
        setLoading(sess, false, added);
        if (sess.button) sess.button.disabled = false;

        if (!sess.nextUrl) {
          if (sess.button && document.activeElement === sess.button && sess.grid) {
            var focusLink = sess.grid.querySelector('li.grid__item:last-child a[href]');
            if (focusLink) focusLink.focus();
          }
          finish(sess);
          return;
        }

        if (auto && sess.pagesLoaded >= AUTO_PAGE_LIMIT) {
          enterButtonMode(sess);
          return;
        }

        if (!auto) {
          enterButtonMode(sess);
          return;
        }

        observe(sess);
      })
      .catch(function (err) {
        if (sess.dead || (err && err.name === 'AbortError')) return;
        sess.inFlight = false;
        setLoading(sess, false);
        if (sess.button) sess.button.disabled = false;
        restorePagination(sess.pagination);
        announce(sess, 'Unable to load more products. Use page numbers to continue.');
      });
  }

  function startSession() {
    abortSession();

    var container = document.getElementById('ProductGridContainer');
    var grid = document.getElementById('product-grid');
    if (!container || !grid || grid.tagName !== 'UL') return;

    var pagination = container.querySelector('.pagination-wrapper');
    if (!pagination) return;

    var nextUrl = readNextUrl(container);
    if (!nextUrl) return;

    var root = container.querySelector('[data-collection-infinite]');
    if (!root) return;

    var sentinel = root.querySelector('[data-infinite-sentinel]');
    var status = root.querySelector('[data-infinite-status]');
    var button = root.querySelector('[data-infinite-load-more]');
    if (!sentinel || !status || !button) return;

    session = {
      dead: false,
      inFlight: false,
      controller: typeof AbortController === 'function' ? new AbortController() : { abort: function () {}, signal: undefined },
      io: null,
      grid: grid,
      pagination: pagination,
      root: root,
      sentinel: sentinel,
      status: status,
      button: button,
      nextUrl: nextUrl,
      pagesLoaded: 1
    };

    hidePagination(pagination);
    root.hidden = false;
    button.hidden = true;
    button.disabled = false;
    button.onclick = function () {
      if (!session || session.dead || session.button !== button) return;
      loadBatch(session, false);
    };

    loadBatch(session, true);
  }

  function queueStart() {
    abortSession();
    if (startQueued) return;
    startQueued = true;
    var run = function () {
      startQueued = false;
      startSession();
    };
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(run);
    } else {
      Promise.resolve().then(run);
    }
  }

  function hookFacetsAbort() {
    if (typeof FacetFiltersForm === 'undefined') return;
    if (FacetFiltersForm.__infiniteScrollBound) return;
    var original = FacetFiltersForm.renderProductGridContainer;
    if (typeof original !== 'function') return;
    FacetFiltersForm.__infiniteScrollBound = true;
    FacetFiltersForm.renderProductGridContainer = function (html) {
      abortSession();
      original.call(this, html);
      queueStart();
    };
  }

  function observeContainer() {
    var container = document.getElementById('ProductGridContainer');
    if (!container || containerObserver) return;
    containerObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length || mutations[i].removedNodes.length) {
          queueStart();
          return;
        }
      }
    });
    containerObserver.observe(container, { childList: true });
  }

  function boot() {
    hookFacetsAbort();
    observeContainer();
    startSession();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
